import React, {FC, useEffect, useState} from 'react'
import {observer} from 'mobx-react'
import useStore from 'store'
import {useTranslation} from 'react-i18next'
import {Space} from '../../ui'
import { Switch } from '@material-ui/core'
import { FeatureIds } from 'utils/types'
import {
    ModalRow,
    ModalText,
    ConfirmButtonSm,
    ButtonGroup,
    DialogHeaderText,
    ToggleGroup,
    ToggleRow
} from './styles'

// Turning this off hides the אלפון tab for the community and redirects the
// route to messages - Tabs and HomeRoutes both read the Contacts feature.

const FEATURE_NAME = 'Contacts'

export interface ContactsFormState {
    isEnable: boolean
}

interface ContactsSectionProps {
    currentOrgId: number | undefined
    resetForm: boolean
}

const ContactsSection: FC<ContactsSectionProps> = ({ currentOrgId, resetForm }) => {
    const {modulesStore, orgs: orgsStore} = useStore()
    const {t} = useTranslation('communitySettings')

    const [isFormChanged, setIsFormChanged] = useState<boolean>(false)
    const [module, setModule] = useState<any>(null)
    const [formState, setFormState] = useState<ContactsFormState>({
        isEnable: false
    })

    useEffect(() => {
        if (!currentOrgId) return

        const contactsFeature = modulesStore.communityFeatures.find(el => el.featureName === FEATURE_NAME)
        if (contactsFeature) {
            setModule(contactsFeature)
            setFormState({ isEnable: contactsFeature.enabled })
        } else {
            // Every community is expected to carry this feature; create it as
            // enabled if one somehow does not, so the tab keeps working.
            orgsStore.updateCommunity(currentOrgId, [...modulesStore.communityFeatures, {
                communityId: currentOrgId,
                enabled: true,
                featureName: FEATURE_NAME,
                id: FeatureIds.CONTACTS,
                status: 1,
            }]).then(async () => {
                await modulesStore.getCommunityModulesAndFeatures(currentOrgId)
                const newContactsFeature = modulesStore.communityFeatures.find(el => el.featureName === FEATURE_NAME)
                if (newContactsFeature) {
                    setModule(newContactsFeature)
                    setFormState({ isEnable: newContactsFeature.enabled })
                }
            })
        }
    }, [currentOrgId])

    useEffect(() => {
        if (resetForm) onResetForm()
    }, [resetForm])

    useEffect(() => {
        setIsFormChanged(
            formState.isEnable !== (module?.enabled ?? false)
        )
    }, [formState, module])

    const Save = async () => {
        if (currentOrgId) {
            await orgsStore.updateCommunity(currentOrgId,
                modulesStore.communityFeatures.map(el => el.featureName === FEATURE_NAME ? {
                    communityId: el.communityId,
                    enabled: formState.isEnable,
                    featureName: el.featureName,
                    id: el.id,
                    status: formState.isEnable ? 1 : 2,
                } : el),)

            await modulesStore.getCommunityModulesAndFeatures(currentOrgId)

            const contactsFeature = modulesStore.communityFeatures.find(el => el.featureName === FEATURE_NAME)
            if (contactsFeature) {
                setModule(contactsFeature)
                setFormState({ isEnable: contactsFeature.enabled })
            }
        }
    }

    const onResetForm = () => setFormState({
        isEnable: module?.enabled ?? false
    })

    const ToggleEnable = (value: any) => setFormState({...formState, isEnable: value })

    return (
        <>
            <ModalRow>
               <DialogHeaderText>{t`contactsTab`}</DialogHeaderText>
               <ButtonGroup>
                 <ConfirmButtonSm disabled={!isFormChanged || module == null} onClick={Save}>{t`save`}</ConfirmButtonSm>
               </ButtonGroup>
            </ModalRow>

            {module &&
                <ToggleGroup>
                    <ToggleRow>
                        <ModalText>{t`isEnable`}:</ModalText>
                        <Switch
                            color="primary"
                            value={formState.isEnable}
                            onChange={({target}) => {
                                ToggleEnable(target.checked)
                            }}
                            checked={formState.isEnable}
                        />
                    </ToggleRow>
                </ToggleGroup>
            }
            <Space/>
            <Space/>
        </>
    )
}

export default observer(ContactsSection)
